import { useCallback } from 'react';
import { nginxApi } from '@/api/nginx';
import { useResource } from '@/hooks/useResource';
import type {
  NginxConfig,
  NginxConfigDetail,
  NginxConfigFilters,
  ReloadNginxResponse,
} from '@/types/nginx';

/**
 * NGINX config list + operations
 */
export function useNginxConfigs(filters: NginxConfigFilters = {}) {
  const { node_id, domain_id, is_active, last_reload_status } = filters;

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await nginxApi.getAll(
        { node_id, domain_id, is_active, last_reload_status },
        { signal }
      );
      return response.configs ?? [];
    },
    [node_id, domain_id, is_active, last_reload_status]
  );

  const resource = useResource<NginxConfig[]>(fetcher, { errorMessage: 'Failed to load configs' });
  const { setData, reload } = resource;

  const generateConfig = useCallback(
    async (domainId: string) => {
      const result = await nginxApi.generate({ domain_id: domainId });
      await reload();
      return result;
    },
    [reload]
  );

  const reloadNginx = useCallback(
    async (nodeId: string): Promise<ReloadNginxResponse> => {
      const result = await nginxApi.reload({ node_id: nodeId });
      await reload();
      return result;
    },
    [reload]
  );

  const activateConfig = useCallback(
    async (id: string) => {
      const result = await nginxApi.activate(id);
      await reload();
      return result;
    },
    [reload]
  );

  const regenerateConfig = useCallback(
    async (id: string) => {
      const result = await nginxApi.regenerate(id);
      await reload();
      return result;
    },
    [reload]
  );

  const deleteConfig = useCallback(
    async (id: string): Promise<void> => {
      await nginxApi.delete(id);
      setData((prev) => prev?.filter((config) => config.id !== id));
    },
    [setData]
  );

  return {
    configs: resource.data ?? [],
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    generateConfig,
    reloadNginx,
    activateConfig,
    regenerateConfig,
    deleteConfig,
  };
}

/**
 * Full content of one config (pass null to disable)
 */
export function useNginxConfigDetail(configId: string | null) {
  const fetcher = useCallback(
    (signal: AbortSignal) => nginxApi.getById(configId as string, { signal }),
    [configId]
  );

  const resource = useResource<NginxConfigDetail>(configId ? fetcher : null, {
    errorMessage: 'Failed to load config',
    keepPreviousData: false,
  });

  return {
    config: resource.data ?? null,
    isLoading: resource.isLoading,
    error: resource.error,
  };
}
